import {
  PLATFORM_DAILY_VISITOR_METRIC_NAME,
  PlatformDailyVisitorMetricResult,
  PlatformDailyVisitorMetricRequest,
} from "@bbb/model";

const COOKIE_NAME = "bbb_platform_daily_visitor_utc_day_v1";

function getApiBaseUrl() {
  const raw = import.meta.env.VITE_DASHBOARD_API_BASE_URL;
  if (!raw) {
    throw new Error(
      "VITE_DASHBOARD_API_BASE_URL is not defined. Please set it in your environment.",
    );
  }

  return raw.replace(/\/$/, "");
}

function getUtcDay(now = new Date()) {
  return now.toISOString().slice(0, 10);
}

function secondsUntilNextUtcDay(now = new Date()) {
  const nextDayStart = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate() + 1,
  );
  return Math.max(60, Math.ceil((nextDayStart - now.getTime()) / 1000));
}

function readCookie(name: string) {
  if (typeof document === "undefined") return null;

  const prefix = `${name}=`;
  const cookie = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));

  return cookie ? decodeURIComponent(cookie.slice(prefix.length)) : null;
}

function writeDailyVisitorCookie(utcDay: string, now = new Date()) {
  if (typeof document === "undefined") return;

  const secure =
    typeof window !== "undefined" && window.location.protocol === "https:"
      ? "; Secure"
      : "";
  document.cookie = [
    `${COOKIE_NAME}=${encodeURIComponent(utcDay)}`,
    "Path=/",
    `Max-Age=${secondsUntilNextUtcDay(now)}`,
    "SameSite=Lax",
    secure,
  ].join("; ");
}

async function postPlatformDailyVisitorMetric(
  payload: PlatformDailyVisitorMetricRequest,
) {
  const response = await fetch(
    `${getApiBaseUrl()}/v1/metrics/platform/daily-visitors`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );

  const body = (await response.json()) as {
    data?: PlatformDailyVisitorMetricResult;
    message?: string;
  };

  if (!response.ok || !body.data) {
    throw new Error(body.message || "Failed to record platform visitor metric");
  }

  return body.data;
}

export async function recordPlatformDailyVisitorOncePerUtcDay(
  now = new Date(),
) {
  const utcDay = getUtcDay(now);
  if (readCookie(COOKIE_NAME) === utcDay) {
    return null;
  }

  const result = await postPlatformDailyVisitorMetric({
    metricName: PLATFORM_DAILY_VISITOR_METRIC_NAME,
    utcDay,
  });
  writeDailyVisitorCookie(utcDay, now);

  return result;
}
